'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Bluetooth, CircleSlash, Cpu, Info, Usb } from 'lucide-react';
import { queryChipIdOverBridge } from '@/hooks/feature/boards/useBoardBridgeChipIdQuery';
import { useDeviceType } from '@/hooks/useIsMobile';
import { apiFetchWithQuery } from '@/lib/api/apiClient';
import { useBoardEditorStore } from '@/stores/useBoardEditorStore';
import type { BoardIdentifyResponse, BoardType, DetectedBoard } from '@/stores/useBoardEditorStore';
import { useModalStore } from '@/stores/useModalStore';
import IconAdaptive from '@/components/IconAdaptive';
import ImageAdaptive from '@/components/ImageAdaptive';
import SectionTitle from '@/components/SectionTitle';
import FormFieldLabel from '@/components/ui/form/FormFieldLabel';
import { REQUIRED_BRIDGE_VERSION } from '@/constants/constants';
import { isVersionOlder } from '@/utils/helpers/bridgeVersionHelper';
import { HUB_AFFILIATION, hubTypeCdToAffiliation, isUsableHubType } from '@/(routers)/boards/editor/block/boardIdentity';
import { BOARD } from '@/(routers)/boards/editor/block/definitions/boards';
import BoardSelectorManual, { BoardValue } from '@/(routers)/boards/editor/components/modal/BoardSelectorManual';

type RowIdentityState = {
    status: 'pending' | 'succeeded' | 'timed_out';
    chipId: number | null;
    identity: BoardIdentifyResponse | null;
};

interface BoardSelectorAutoProps {
    value?: BoardValue;
    onChange: (v: BoardType | null) => void;
    isDisabled?: boolean;
}

const BOARD_UI = {
    orange: { label: 'スクーミーオレンジボード', colorClass: 'bg-color-ide-board-orange' },
    blue: { label: 'スクーミーブルーボード', colorClass: 'bg-color-ide-board-blue' },
} as const;

export default function BoardSelectorAuto({ onChange, isDisabled }: BoardSelectorAutoProps) {
    const [local, setLocal] = useState<BoardValue>(null);
    const closeModal = useModalStore((s) => s.closeModal);
    const { isMobileDevice } = useDeviceType();

    const detectedBoards = useBoardEditorStore((s) => s.detectedBoards);
    const isWebSocketConnected = useBoardEditorStore((s) => s.isWebSocketConnected);
    const isBridgeConnected = useBoardEditorStore((s) => s.isBridgeConnected);
    const bridgeVersion = useBoardEditorStore((s) => s.bridgeVersion);
    const osType = useBoardEditorStore((s) => s.osType);
    const setChipId = useBoardEditorStore((s) => s.setChipId);
    const setBoardIdentity = useBoardEditorStore((s) => s.setBoardIdentity);
    const setChipIdQueryStatus = useBoardEditorStore((s) => s.setChipIdQueryStatus);
    // 旧ブリッジ(read_chip_id未対応)は問い合わせに無応答なだけなので、対応が確認できるまでは
    // 問い合わせ自体を送らず、ブルーもオレンジ同様「接続中」のまま選択できるようにする
    // (BoardToolbar.tsxのbridgeSupportsChipIdと同じ考え方)。
    const bridgeSupportsChipId = !!bridgeVersion && !isVersionOlder(bridgeVersion, REQUIRED_BRIDGE_VERSION);

    const handlePick = (v: BoardValue) => {
        setLocal(v);
        onChange(v);
        if (v) closeModal?.();
    };

    // detectedBoards自体が変わっていないのに毎レンダー新しい配列になると、下のeffectが
    // 無関係な再レンダー(identityByPortの更新等)のたびに走ってしまうため、内容が変わった
    // ときだけ再計算する。
    const usbBoards = useMemo(() => detectedBoards.filter((b) => !b.is_bluetooth), [detectedBoards]);

    // 一覧の各行(ブルーボードのみ)ごとに、ネイティブピッカーを挟まず即座にchip_idを問い合わせて
    // cd/BT名/hub所属を解決する。ブリッジはUSB接続した時点でポートに直接シリアル通信できるため、
    // Web Serial APIと違い「選んで初めて実機と話せる」制約が無く、一覧表示中でも先に確認できる。
    const [identityByPort, setIdentityByPort] = useState<Record<string, RowIdentityState>>({});
    // 同じポートへ二重に問い合わせないためのガード。stateの更新は非同期なので、レンダー間で
    // 確実に一度きりにするにはrefで管理する必要がある。
    const queriedPortsRef = useRef<Set<string>>(new Set());
    // ポートが一瞬detectedBoardsから消えて再度現れた場合、queriedPortsRefから外れ→再度
    // 問い合わせが走る。先行分の遅延応答が後着の応答をidentityByPortで上書きしないよう、
    // ポートごとに問い合わせの世代番号を持たせ、応答適用時に現在の世代と一致するかを照合する。
    const queryGenerationRef = useRef(0);
    const portGenerationRef = useRef<Record<string, number>>({});
    // Strict Mode(開発時)はマウント→クリーンアップ→再マウントを疑似実行するため、
    // クリーンアップのみでfalseにする実装だと再マウント時にtrueへ戻す処理が無く、
    // 実際は正しく生存しているのに永久にfalseのままになるバグがあった。
    // effect本体でも明示的にtrueへセットすることで、再マウント時に正しく復元されるようにする。
    const isMountedRef = useRef(true);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!bridgeSupportsChipId) return;
        const bluePorts = new Set(usbBoards.filter((b) => b.type === 'blue').map((b) => b.port));

        bluePorts.forEach((port) => {
            if (queriedPortsRef.current.has(port)) return;
            queriedPortsRef.current.add(port);
            const generation = ++queryGenerationRef.current;
            portGenerationRef.current[port] = generation;
            setIdentityByPort((prev) => ({ ...prev, [port]: { status: 'pending', chipId: null, identity: null } }));

            (async () => {
                const isCurrent = () => isMountedRef.current && portGenerationRef.current[port] === generation;
                const chipId = await queryChipIdOverBridge(port);
                if (!isCurrent()) return;
                if (chipId === null) {
                    setIdentityByPort((prev) => ({
                        ...prev,
                        [port]: { status: 'timed_out', chipId: null, identity: null },
                    }));
                    return;
                }
                // BoardToolbar.tsxのchip_id問い合わせと同じ規約: statusの成否はchip_idが取れたか
                // どうかのみで決まる(以後の/boards/identify自体の成否では変えない)。取得失敗時は
                // identityがnullのまま'succeeded'扱いにし、hub所属は「確定して未所属」と同じ
                // unknown表示になる。
                try {
                    const identity = (await apiFetchWithQuery('/boards/identify', {
                        chip_id: chipId,
                    })) as BoardIdentifyResponse;
                    if (!isCurrent()) return;
                    setIdentityByPort((prev) => ({ ...prev, [port]: { status: 'succeeded', chipId, identity } }));
                } catch {
                    if (!isCurrent()) return;
                    setIdentityByPort((prev) => ({
                        ...prev,
                        [port]: { status: 'succeeded', chipId, identity: null },
                    }));
                }
            })();
        });

        // 抜けたポートは問い合わせ済み記録からも外し、再度挿さった時に取り直せるようにする
        queriedPortsRef.current.forEach((port) => {
            if (!bluePorts.has(port)) queriedPortsRef.current.delete(port);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usbBoards, bridgeSupportsChipId]);

    // 一覧から選んだ時点で、この行のために既に解決済みのchip_id/boardIdentityがあれば
    // 親(BoardToolbar.tsx)のストアへそのまま引き継ぐ。これが無いと、選択直後に
    // BoardToolbar.tsx側の問い合わせeffect(chipIdQueryStatus==='idle'が条件)が
    // 同じポートへ二重に問い合わせをやり直してしまう(bridgeのstart_serialは
    // reference countされていないため、二重の問い合わせ自体もポート横取りのリスクがある)。
    const handlePickDetected = (board: DetectedBoard) => {
        const rowIdentity = board.type === 'blue' ? identityByPort[board.port] : undefined;
        if (rowIdentity && rowIdentity.status !== 'pending') {
            setChipId(rowIdentity.chipId);
            setBoardIdentity(rowIdentity.identity);
            setChipIdQueryStatus(rowIdentity.status);
        }
        handlePick(board.type);
    };

    const renderAutoSelectBoards = () => {
        if (!isBridgeConnected) {
            return (
                <div className="flex w-full">
                    <div className="flex flex-grow justify-start items-center border border-red-300 rounded-lg h-[70px] p-4 bg-red-50">
                        <div className="flex justify-center items-center px-1">
                            <div className="flex justify-center items-center rounded-full w-[40px] h-[40px] bg-red-200">
                                <ImageAdaptive
                                    src="/images/common/loading-circle.svg"
                                    baseWidth={32}
                                    breakpoints={{ md: 32 }}
                                />
                            </div>
                        </div>
                        <div className="flex flex-grow justify-start font-notojp font-medium text-red-700 text-sm pl-4">
                            スクーミーブリッジが起動していません。接続を待機中・・・
                        </div>
                    </div>
                </div>
            );
        }

        if (!isWebSocketConnected) {
            return (
                <div className="flex w-full">
                    <div className="flex flex-grow justify-start items-center border border-gray-300 rounded-lg h-[70px] p-4 bg-gray-50">
                        <div className="flex justify-center items-center px-1">
                            <div className="flex justify-center items-center rounded-full w-[40px] h-[40px] bg-gray-200">
                                <ImageAdaptive
                                    src="/images/common/loading-circle.svg"
                                    baseWidth={32}
                                    breakpoints={{ md: 32 }}
                                />
                            </div>
                        </div>
                        <div className="flex flex-grow justify-start font-notojp font-medium text-color-co-gray-dark text-sm pl-4">
                            接続中...
                        </div>
                    </div>
                </div>
            );
        }

        if (usbBoards.length === 0) {
            return (
                <div className="flex w-full">
                    <div className="flex flex-grow justify-start items-center border border-gray-300 rounded-lg h-[70px] p-4 bg-gray-50">
                        <div className="flex justify-center items-center px-1">
                            <div className="flex justify-center items-center rounded-full w-[40px] h-[40px] bg-gray-200">
                                <ImageAdaptive
                                    src="/images/common/loading-circle.svg"
                                    baseWidth={32}
                                    breakpoints={{ md: 32 }}
                                />
                            </div>
                        </div>
                        <div className="flex flex-grow justify-start font-notojp font-medium text-color-co-gray-dark text-sm pl-4">
                            ボードが検出できません。USBケーブルで接続してください。
                        </div>
                    </div>
                </div>
            );
        }

        return usbBoards.map((board: DetectedBoard) => {
            const boardUI = BOARD_UI[board.type];
            const rowIdentity = board.type === 'blue' && bridgeSupportsChipId ? identityByPort[board.port] : undefined;
            // hub所属バッジ: pending中は非表示、確定(succeeded)して使用可能な3種のいずれかなら色付き、
            // それ以外(timed_out、または確定して未所属/未対応hub種別)はグレーの「unknown」表示にする
            // (BoardToolbar.tsxのHubAffiliationIconと同じ確定状態の扱い方に揃える)
            const affiliation =
                rowIdentity?.status === 'succeeded' ? hubTypeCdToAffiliation(rowIdentity.identity?.hub_type_cd) : null;
            const isUsable =
                rowIdentity?.status === 'succeeded' && isUsableHubType(rowIdentity.identity?.hub_type_cd);
            // 未解決(pending)・取得失敗(timed_out)の間はcd/BT名を出さない(検証できていない値は
            // 表示しない)。hub所属がunknown(未所属/未対応)の間も同様に出さない。BT名はcdが
            // 無くても決定論的に計算できてしまうため、hub所属の判定と無関係に出てしまわないよう
            // 明示的にゲートする。
            const hasBoardName = isUsable && rowIdentity?.identity?.cd != null;
            const hasBluetoothName = isUsable && !!rowIdentity?.identity?.bluetooth_device_name;
            // ブルーボードは問い合わせ確定(succeeded/timed_out)まで、cd/BT名だけでなくPort表示も
            // 一緒に隠す。Portだけ先に出て後からcd/BT名が追いつくとチラつきが目立つため、
            // 確定した情報をまとめて一度に出す。rowIdentity未生成(問い合わせ開始直後の一瞬)もpending扱い
            const isPending =
                board.type === 'blue' &&
                bridgeSupportsChipId &&
                rowIdentity?.status !== 'succeeded' &&
                rowIdentity?.status !== 'timed_out';
            // 確認は完了したが使用不可と確定した状態。「接続中」は誤解を招くため別文言にする
            // (BoardToolbar.tsxのstatusTextの「利用不可」と同じ考え方)。オレンジは対象外。
            // 旧ブリッジ(bridgeSupportsChipId=false)は問い合わせ自体をしないため、この判定
            // 対象にせず常にオレンジ同様「接続中」のまま選択可能にする(後方互換)。
            const isConfirmedUnusable = board.type === 'blue' && bridgeSupportsChipId && !isPending && !isUsable;
            const statusLabel = isPending ? '確認中...' : isConfirmedUnusable ? '利用不可' : '接続中';

            return (
                <div
                    key={board.port}
                    className={`flex w-full mb-2 ${isPending ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                    onClick={() => !isPending && handlePickDetected(board)}
                >
                    <div className="flex flex-grow justify-start items-center border border-gray-300 rounded-lg h-[70px] shadow-right-bottom-mid p-4 bg-white">
                        <div className="flex justify-center items-center px-1">
                            <img
                                src={BOARD[board.type].icon.src}
                                style={{ width: 52, height: 52, objectFit: 'contain' }}
                                alt=""
                            />
                        </div>

                        <div className="flex flex-col flex-grow justify-start pl-5 pr-8">
                            <div className="flex items-center gap-2">
                                <span className="font-corp font-medium text-color-base text-sm">{boardUI.label}</span>
                                <div className="flex items-center gap-1">
                                    {/* トールバー本体はドット⇔スピナーで幅を揃えていないため文字がズレるが、
                                        こちらは行内でラベルが横に並ぶレイアウトのためズレが目立つ。
                                        18px固定幅のコンテナに揃えて、切り替え時に文字が動かないようにする */}
                                    <div className="flex items-center justify-center" style={{ width: 13, height: 13 }}>
                                        {isPending ? (
                                            <ImageAdaptive
                                                src="/images/common/loading-circle.svg"
                                                baseWidth={13}
                                                breakpoints={{ md: 13 }}
                                            />
                                        ) : (
                                            <motion.div
                                                className={`w-1.5 h-1.5 rounded-full ${
                                                    isConfirmedUnusable ? 'bg-gray-300' : 'bg-color-ide-board-connected'
                                                }`}
                                                style={{ marginTop: -1 }}
                                                animate={isConfirmedUnusable ? { opacity: 1 } : { opacity: [1, 0.3, 1] }}
                                                transition={
                                                    isConfirmedUnusable
                                                        ? { duration: 0 }
                                                        : { duration: 2, repeat: Infinity, ease: 'easeInOut' }
                                                }
                                            />
                                        )}
                                    </div>
                                    <span
                                        className={`font-cantarell font-medium text-[11px] ${
                                            isPending || isConfirmedUnusable ? 'text-gray-400' : 'text-color-ide-board-connected'
                                        }`}
                                    >
                                        {statusLabel}
                                    </span>
                                </div>
                            </div>

                            {/* pending中も高さを確保しておく(minHeight)ことで、確定して中身が
                                入った瞬間に行の高さが変わって上のラベルごと位置が動くのを防ぐ */}
                            <div className="flex items-center flex-wrap mt-1" style={{ minHeight: 26 }}>
                                {!isPending && (
                                    <motion.div
                                        className="flex items-center flex-wrap"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        {hasBoardName && (
                                            <>
                                                <div className="flex justify-center items-center w-[26px] h-[26px]">
                                                    <IconAdaptive
                                                        Icon={Cpu}
                                                        strokeWidth={2.0}
                                                        baseSize={20}
                                                        breakpoints={{ md: 20 }}
                                                        className="text-color-base"
                                                    />
                                                </div>
                                                <span className="font-cantarell font-medium text-color-co-gray-dark text-sm pr-2">
                                                    {String(rowIdentity!.identity!.cd).padStart(5, '0')}
                                                </span>
                                            </>
                                        )}
                                        {hasBluetoothName && (
                                            <>
                                                <div className="flex justify-center items-center w-[26px] h-[26px]">
                                                    <IconAdaptive
                                                        Icon={Bluetooth}
                                                        strokeWidth={2.0}
                                                        baseSize={20}
                                                        breakpoints={{ md: 20 }}
                                                        className="text-color-base"
                                                    />
                                                </div>
                                                <span className="font-cantarell font-medium text-color-co-gray-dark text-sm pr-2">
                                                    {rowIdentity!.identity!.bluetooth_device_name}
                                                </span>
                                            </>
                                        )}
                                        <div className="flex justify-center items-center w-[26px] h-[26px]">
                                            <IconAdaptive
                                                Icon={Usb}
                                                strokeWidth={2.0}
                                                baseSize={20}
                                                breakpoints={{ md: 20 }}
                                                className="text-color-base"
                                            />
                                        </div>
                                        <span className="font-cantarell font-medium text-color-co-gray-dark text-sm">
                                            {board.port}
                                        </span>
                                    </motion.div>
                                )}
                            </div>
                        </div>

                        {/* pending中(未確定)は非表示。確定後のみ、使用可能なら色付き、それ以外はunknown(グレー)を出す */}
                        {!isPending && rowIdentity && (
                            <motion.div
                                className="flex justify-center items-center px-2"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.3 }}
                            >
                                <span
                                    className="flex items-center justify-center rounded-full flex-shrink-0"
                                    style={{
                                        width: 36,
                                        height: 36,
                                        backgroundColor: isUsable && affiliation ? HUB_AFFILIATION[affiliation].color : '#9CA3AF',
                                    }}
                                >
                                    <IconAdaptive
                                        Icon={isUsable && affiliation ? HUB_AFFILIATION[affiliation].Icon : CircleSlash}
                                        strokeWidth={2.0}
                                        baseSize={22}
                                        breakpoints={{ md: 22 }}
                                        className="text-white"
                                    />
                                </span>
                            </motion.div>
                        )}
                    </div>
                </div>
            );
        });
    };

    return (
        <div className="flex flex-col w-full md:w-[98%] section-4-b bg-color-white-base">
            <div className="flex w-full items-center">
                <div className="flex flex-grow justify-start items-center w-full">
                    <SectionTitle title="Select SchooMyBoard" />
                </div>
            </div>

            <div className="flex w-full items-center pt-4 pb-2">
                <div className="flex flex-grow justify-start items-start w-full font-notojp font-medium text-sm text-color-base">
                    <div className="flex flex-col justify-start items-start">
                        使用するスクーミーボードを選択してください。
                    </div>
                </div>
            </div>

            <div className="flex flex-col w-full">
                <div className="section-spacer-inner-y"></div>
                <div className="flex justify-start items-center w-full py-2">
                    <FormFieldLabel title="自動選択" />
                </div>
                <div className="flex w-full items-center">
                    <div className="flex flex-grow justify-start items-start w-full font-notojp font-medium text-xs text-color-base">
                        <div className="flex justify-start items-start">
                            <IconAdaptive
                                Icon={Info}
                                strokeWidth={2.0}
                                baseSize={20}
                                breakpoints={{ md: 20 }}
                                className="mr-[8px]"
                            />
                        </div>
                        <div className="flex flex-col justify-start items-start">
                            <div className="flex items-center mt-[1.5px]">
                                スクーミーボードとPCをUSB接続するとボードの種類、ポート番号が自動判別されます。
                            </div>
                        </div>
                    </div>
                </div>
                <div className="section-spacer-inner-y" />
                {renderAutoSelectBoards()}
                {!isMobileDevice && !isBridgeConnected && osType !== 'chromebook' && (
                    <div className="flex justify-center font-notojp font-medium text-xs text-color-base mt-2">
                        スクーミーボードの利用には、スクーミーブリッジのインストールが必要です。
                    </div>
                )}
            </div>

            <div className="flex justify-center w-full">
                <div className="flex items-center justify-center py-2 w-[90%]">
                    <div className="flex-grow border-t border-gray-300"></div>
                    <span className="font-montserrat font-bold tracing-widest text-base mx-4 text-gray-300">OR</span>
                    <div className="flex-grow border-t border-gray-300"></div>
                </div>
            </div>

            <div className="flex flex-col w-full">
                <div className="flex justify-start items-center w-full py-2">
                    <FormFieldLabel title="手動選択" />
                </div>
                <div className="flex w-full items-center">
                    <div className="flex flex-grow justify-start items-start w-full font-notojp font-medium text-xs text-color-base">
                        <div className="flex justify-start items-start">
                            <IconAdaptive
                                Icon={Info}
                                strokeWidth={2.0}
                                baseSize={20}
                                breakpoints={{ md: 20 }}
                                className="mr-[8px]"
                            />
                        </div>
                        <div className="flex items-center mt-[1.5px]">
                            該当するボードが自動選択されない場合は手動で選択してください。
                        </div>
                    </div>
                </div>
                <div className="section-spacer-inner-y">
                    <BoardSelectorManual value={local} onChange={handlePick} isDisabled={isDisabled} />
                </div>
            </div>
        </div>
    );
}
